import { router } from "@inertiajs/react"
import { type FormEvent, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PasswordInput } from "@/components/ui/password-input"
import { Spinner } from "@/components/ui/spinner"
import { useCsrfToken } from "@/hooks/use-csrf-token"

interface SignupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SignupModal({ open, onOpenChange }: SignupModalProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const csrfToken = useCsrfToken()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    setErrors({})

    try {
      const response = await fetch("/sign-up", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ email, password }),
      })

      if (response.ok) {
        onOpenChange(false)
        router.reload()
      } else {
        const data = (await response.json()) as {
          errors?: Record<string, string[]>
        }
        if (data.errors) {
          setErrors(data.errors)
        }
      }
    } catch {
      setErrors({ base: ["Something went wrong. Please try again."] })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create a free account</DialogTitle>
          <DialogDescription>
            Sign up to download your file and access all features
          </DialogDescription>
        </DialogHeader>

        <form method="post" action="/auth/google_oauth2">
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button type="submit" variant="outline" className="w-full">
            <GoogleIcon />
            Continue with Google
          </Button>
        </form>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">Or</span>
          </div>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-4"
        >
          <div className="grid gap-2">
            <Label htmlFor="modal-email">Email address</Label>
            <Input
              id="modal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="email@example.com"
            />
            {errors.email && (
              <p className="text-destructive text-sm">{errors.email[0]}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="modal-password">Password</Label>
            <PasswordInput
              id="modal-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Password"
            />
            {errors.password && (
              <p className="text-destructive text-sm">{errors.password[0]}</p>
            )}
          </div>

          {errors.base && (
            <p className="text-destructive text-sm">{errors.base[0]}</p>
          )}

          <Button type="submit" className="w-full" disabled={processing}>
            {processing && <Spinner />}
            Create account
          </Button>

          <p className="text-muted-foreground text-center text-xs">
            Already have an account?{" "}
            <a href="/sign-in" className="text-primary hover:underline">
              Log in
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function GoogleIcon() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}
